import type { QuestionOption } from '@common/types/templateFormFields';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { useRef } from 'react';
import { errorSnackbar } from '@/utils/snackbar';
import ChoiceField from './ChoiceField';
import type { OptionEditorFieldData, OptionErrorType } from './types';

interface QuestionChoiceComponentProps {
    isSuperAdmin?: boolean;
    disabled?: boolean;
    fieldData?: OptionEditorFieldData;
    choiceOptions: QuestionOption[];
    error: OptionErrorType[];
    onChange: (options: QuestionOption[]) => void;
    onError: (error: OptionErrorType[]) => void;
}

export default function QuestionChoiceComponent({
    isSuperAdmin,
    disabled,
    fieldData,
    error,
    onError,
    choiceOptions,
    onChange,
}: QuestionChoiceComponentProps) {
    const dragIndex = useRef<number | undefined>(undefined);
    const handleOnChange = (value: string, index: number) => {
        const sameOption = choiceOptions.find((op) => op?.optionLabel === value);
        if (sameOption) {
            onError([...error, { message: 'Option already exists', state: true, index: index }]);
        } else {
            onError(error.filter((err) => err.index !== index));
        }
        const updatedChoices = choiceOptions.map((op, i) => {
            if (i === index) {
                return {
                    ...op,
                    optionLabel: value,
                    optionValue: value,
                };
            }
            return op;
        });

        onChange(updatedChoices);
    };

    const handleOnRemove = (index: number) => {
        if (choiceOptions.length <= 2) {
            //Display error message is options are less than 2
            errorSnackbar('Deletion failed. A minimum of two options is required.');
            return;
        }
        const updatedChoices = choiceOptions.filter((_option, i) => i !== index);
        onError(error.filter((err) => err.index !== index)); //clear error message of removed option
        onChange(updatedChoices);
    };

    //On drop swap the elements in the array
    const handleDrop = (dropIndex: number) => {
        const updatedOptions = [...choiceOptions];
        if (dragIndex.current !== undefined) {
            //Remove the dragged Item from the array.
            const draggedItem = updatedOptions.splice(dragIndex.current, 1)[0];

            //Add the dragged Item from the array and place it on drop index and update index for rest of the items.
            updatedOptions.splice(dropIndex, 0, draggedItem);

            //update the dragIndex to undefined
            dragIndex.current = undefined;
        }

        onChange(updatedOptions);
    };

    //Disable add button
    const isDisabledAddButton =
        disabled ||
        choiceOptions.some((option) => !option.optionLabel) || //check for empty input
        choiceOptions.some((option, index) =>
            choiceOptions
                .slice(0, index)
                .some((prevOption) => prevOption.optionLabel === option.optionLabel),
        ); //check for duplicates

    const customChoiceOptions = Array.from({ length: 10 }, (_, i) => ({
        optionLabel: `${i + 1}`,
        optionValue: `${i + 1}`,
    }));
    return (
        <Box
            component={'div'}
            onDragOver={(e) => {
                e.preventDefault();
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '20px',
            }}
        >
            {fieldData?.name === 'purchasersListedOnPurchaseAndSaleAgreement' ||
            fieldData?.name === 'borrowersList' ||
            fieldData?.name === 'sellersListedOnPurchaseAndSaleAgreement'
                ? customChoiceOptions?.map((choice, index) => (
                      <ChoiceField
                          key={index}
                          fieldData={fieldData}
                          error={error.find((err) => err.index === index)}
                          index={index + 1}
                          option={choice}
                          onChange={(value: string) => handleOnChange(value, index)}
                          onRemove={() => handleOnRemove(index)}
                          onDragStart={(index: number) => (dragIndex.current = index)}
                          onDrop={(index) => handleDrop(index)}
                          isSuperAdmin={isSuperAdmin}
                      />
                  ))
                : choiceOptions?.map((choice, index) => (
                      <ChoiceField
                          key={index}
                          fieldData={fieldData}
                          error={error.find((err) => err.index === index)}
                          index={index + 1}
                          option={choice}
                          onChange={(value: string) => handleOnChange(value, index)}
                          onRemove={() => handleOnRemove(index)}
                          onDragStart={(index: number) => (dragIndex.current = index)}
                          onDrop={(index) => handleDrop(index)}
                          isSuperAdmin={isSuperAdmin}
                      />
                  ))}
            <Button
                disabled={
                    fieldData?.questionSchema?.restrictOptionEdit
                        ? true
                        : isSuperAdmin
                          ? false
                          : isDisabledAddButton
                }
                sx={{
                    textTransform: 'initial',
                }}
                variant="text"
                onClick={() =>
                    onChange([
                        ...choiceOptions,
                        {
                            optionLabel: '',
                            optionValue: '',
                            isNewAdded: true,
                        },
                    ])
                }
            >
                <AddIcon /> Add option
            </Button>
        </Box>
    );
}
